import { useEffect, useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../../lib/useClickOutside';
import { COLUMNS, TOP_GROUPS, type ColumnKey, type TopGroupKey } from './columns';

type Props = {
  topGroup: TopGroupKey;
  columnOrder: readonly ColumnKey[];
  visibleColumns: ReadonlySet<ColumnKey>;
  onVisibleChange: (next: Set<ColumnKey>) => void;
};

// Per-group cog. Appears in the top-group header row at low opacity; hover
// lifts to full. Click opens a popover with:
//   - checkbox list of columns in the group (or nested per sub-group for
//     Budget / Execution)
//   - "Show all" / "Hide all" per section
//
// Reorder was removed in favour of a single grid-level "Reorder groups"
// modal (see GroupReorderModal.tsx). Columns within a group are fixed in
// factory order -- users found per-column reordering more confusing than
// useful, especially inside Budget/Execution where sub-group parity matters.
//
// Closes on click-outside and Escape.

export function GroupCogMenu({
  topGroup,
  columnOrder,
  visibleColumns,
  onVisibleChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);

  useClickOutside([rootRef], () => setOpen(false), open);

  // Esc close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const tg = TOP_GROUPS.find((g) => g.key === topGroup)!;

  // Columns belonging to this top group, preserving the current column order.
  const groupColumns = useMemo(() => {
    const keys = new Set(
      COLUMNS.filter((c) => c.topGroup === topGroup).map((c) => c.key),
    );
    return columnOrder.filter((k) => keys.has(k));
  }, [columnOrder, topGroup]);

  const toggle = (key: ColumnKey) => {
    const next = new Set(visibleColumns);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onVisibleChange(next);
  };
  const setBulk = (keys: readonly ColumnKey[], visible: boolean) => {
    const next = new Set(visibleColumns);
    for (const k of keys) {
      if (visible) next.add(k);
      else next.delete(k);
    }
    onVisibleChange(next);
  };

  return (
    <span ref={rootRef} className="relative inline-block ml-1">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label={`Column settings for ${tg.label}`}
        aria-expanded={open}
        className="text-slate-400 hover:text-slate-700 opacity-40 hover:opacity-100 transition-opacity text-sm px-1"
      >
        ⚙
      </button>

      {open && (
        <div
          role="menu"
          className="absolute z-30 top-full mt-1 left-1/2 -translate-x-1/2 w-64 rounded-md border border-slate-300 bg-white shadow-lg p-2 text-sm text-left"
        >
          <div className="pb-2 mb-2 border-b border-slate-100">
            <span className="text-xs uppercase tracking-wide font-medium text-slate-600">
              {tg.label}
            </span>
          </div>

          {tg.subGroups.length === 0 && (
            <ToggleList
              columns={groupColumns}
              visibleColumns={visibleColumns}
              onToggle={toggle}
              onShowAll={() => setBulk(groupColumns, true)}
              onHideAll={() => setBulk(groupColumns, false)}
            />
          )}

          {tg.subGroups.length > 0 && (
            <div className="space-y-3">
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setBulk(groupColumns, true)}
                  className="text-slate-600 hover:text-slate-900 underline"
                >
                  Show all in group
                </button>
                <button
                  type="button"
                  onClick={() => setBulk(groupColumns, false)}
                  className="text-slate-600 hover:text-slate-900 underline"
                >
                  Hide all in group
                </button>
              </div>
              {tg.subGroups.map((sg) => {
                const subCols = groupColumns.filter(
                  (k) => COLUMNS.find((c) => c.key === k)?.subGroup === sg.key,
                );
                return (
                  <div key={sg.key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs uppercase tracking-wide font-medium text-slate-500">
                        {sg.label}
                      </span>
                      <div className="flex gap-2 text-[10px]">
                        <button
                          type="button"
                          onClick={() => setBulk(subCols, true)}
                          className="text-slate-500 hover:text-slate-900 underline"
                        >
                          Show all
                        </button>
                        <button
                          type="button"
                          onClick={() => setBulk(subCols, false)}
                          className="text-slate-500 hover:text-slate-900 underline"
                        >
                          Hide all
                        </button>
                      </div>
                    </div>
                    <ToggleList
                      columns={subCols}
                      visibleColumns={visibleColumns}
                      onToggle={toggle}
                      hideBulkButtons
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </span>
  );
}

function ToggleList({
  columns,
  visibleColumns,
  onToggle,
  onShowAll,
  onHideAll,
  hideBulkButtons = false,
}: {
  columns: ColumnKey[];
  visibleColumns: ReadonlySet<ColumnKey>;
  onToggle: (key: ColumnKey) => void;
  onShowAll?: () => void;
  onHideAll?: () => void;
  hideBulkButtons?: boolean;
}) {
  return (
    <div className="space-y-0.5">
      {!hideBulkButtons && (onShowAll || onHideAll) && (
        <div className="flex gap-2 pb-2 mb-2 border-b border-slate-100 text-xs">
          {onShowAll && (
            <button
              type="button"
              onClick={onShowAll}
              className="text-slate-600 hover:text-slate-900 underline"
            >
              Show all
            </button>
          )}
          {onHideAll && (
            <button
              type="button"
              onClick={onHideAll}
              className="text-slate-600 hover:text-slate-900 underline"
            >
              Hide all
            </button>
          )}
        </div>
      )}
      {columns.map((key) => {
        const def = COLUMNS.find((c) => c.key === key)!;
        const checked = visibleColumns.has(key);
        return (
          <label
            key={key}
            className="flex items-center gap-2 px-1 py-1 hover:bg-slate-50 rounded cursor-pointer"
          >
            <input type="checkbox" checked={checked} onChange={() => onToggle(key)} />
            <span className="truncate">{def.label}</span>
          </label>
        );
      })}
    </div>
  );
}
